<template>
  <div class="news-import">
    <div class="import-header">
      <h3 class="import-title">
        <i class="fas fa-file-import"></i>
        Importar Noticias
      </h3>
      <button @click="$emit('close')" class="close-button" title="Cerrar">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="import-body">
      <!-- Step 1: Upload File -->
      <div v-if="step === 1" class="import-step">
        <div class="step-header">
          <div class="step-number">1</div>
          <div class="step-info">
            <h4>Selecciona el archivo</h4>
            <p>Soporta archivos JSON y CSV con noticias</p>
          </div>
        </div>

        <div class="file-upload-zone" :class="{ 'drag-over': isDragging }">
          <input
            ref="fileInput"
            type="file"
            accept=".json,.csv"
            @change="handleFileSelect"
            class="file-input"
          />
          <div
            class="upload-area"
            @click="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleFileDrop"
          >
            <i class="fas fa-cloud-upload-alt upload-icon"></i>
            <p class="upload-text">Arrastra tu archivo aquí o haz clic para seleccionar</p>
            <p class="upload-hint">JSON o CSV (máx. 5MB)</p>
          </div>
        </div>

        <div v-if="selectedFile" class="selected-file">
          <div class="file-icon">
            <i :class="selectedFile.name.endsWith('.json') ? 'fas fa-file-code' : 'fas fa-file-csv'"></i>
          </div>
          <div class="file-info">
            <div class="file-name">{{ selectedFile.name }}</div>
            <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
          </div>
          <button @click="removeFile" class="remove-file" title="Eliminar">
            <i class="fas fa-trash-alt"></i>
          </button>
        </div>

        <div class="format-guide">
          <h5>Formato esperado:</h5>
          <div class="format-tabs">
            <button
              @click="activeFormat = 'json'"
              :class="['format-tab', { active: activeFormat === 'json' }]"
            >
              JSON
            </button>
            <button
              @click="activeFormat = 'csv'"
              :class="['format-tab', { active: activeFormat === 'csv' }]"
            >
              CSV
            </button>
          </div>

          <div v-if="activeFormat === 'json'" class="format-example">
            <pre><code>[
  {
    "title": "Título de la noticia",
    "content": "Contenido HTML de la noticia",
    "createdAt": "2024-01-15T10:30:00" (opcional)
  },
  ...
]</code></pre>
          </div>

          <div v-else class="format-example">
            <pre><code>title,content,createdAt
"Título 1","&lt;p&gt;Contenido HTML&lt;/p&gt;","2024-01-15T10:30:00"
"Título 2","&lt;p&gt;Otro contenido&lt;/p&gt;",""</code></pre>
          </div>
        </div>

        <div class="step-actions">
          <button @click="parseFile" :disabled="!selectedFile || parsing" class="btn-primary">
            <span v-if="parsing">
              <i class="fas fa-spinner fa-spin"></i>
              Procesando...
            </span>
            <span v-else>
              <i class="fas fa-arrow-right"></i>
              Continuar
            </span>
          </button>
        </div>
      </div>

      <!-- Step 2: Preview and Validate -->
      <div v-if="step === 2" class="import-step">
        <div class="step-header">
          <div class="step-number">2</div>
          <div class="step-info">
            <h4>Revisa los datos</h4>
            <p>{{ parsedNews.length }} noticias encontradas</p>
          </div>
        </div>

        <div class="validation-summary">
          <div class="summary-item valid">
            <i class="fas fa-check-circle"></i>
            <span>{{ validNews.length }} válidas</span>
          </div>
          <div v-if="invalidNews.length > 0" class="summary-item invalid">
            <i class="fas fa-exclamation-circle"></i>
            <span>{{ invalidNews.length }} con errores</span>
          </div>
        </div>

        <div class="news-preview-list">
          <div
            v-for="(item, index) in parsedNews"
            :key="index"
            :class="['news-preview-item', { invalid: item.error }]"
          >
            <div class="preview-header">
              <div class="preview-status">
                <i v-if="!item.error" class="fas fa-check-circle valid-icon"></i>
                <i v-else class="fas fa-times-circle invalid-icon"></i>
              </div>
              <div class="preview-title">{{ item.title || 'Sin título' }}</div>
            </div>
            <div v-if="item.error" class="preview-error">
              <i class="fas fa-exclamation-triangle"></i>
              {{ item.error }}
            </div>
            <div v-else class="preview-meta">
              <span class="preview-content-length">
                {{ item.content ? item.content.length : 0 }} caracteres
              </span>
              <span v-if="item.createdAt" class="preview-date">
                {{ formatDate(new Date(item.createdAt)) }}
              </span>
            </div>
          </div>
        </div>

        <div class="step-actions">
          <button @click="step = 1" class="btn-secondary">
            <i class="fas fa-arrow-left"></i>
            Volver
          </button>
          <button
            @click="importNews"
            :disabled="validNews.length === 0 || importing"
            class="btn-primary"
          >
            <span v-if="importing">
              <i class="fas fa-spinner fa-spin"></i>
              Importando {{ importProgress }}/{{ validNews.length }}...
            </span>
            <span v-else>
              <i class="fas fa-download"></i>
              Importar {{ validNews.length }} noticias
            </span>
          </button>
        </div>
      </div>

      <!-- Step 3: Results -->
      <div v-if="step === 3" class="import-step">
        <div class="step-header">
          <div class="step-number">3</div>
          <div class="step-info">
            <h4>Importación completada</h4>
          </div>
        </div>

        <div class="import-results">
          <div class="result-summary success">
            <i class="fas fa-check-circle"></i>
            <div class="result-text">
              <h5>¡Importación exitosa!</h5>
              <p>Se importaron {{ importedCount }} de {{ validNews.length }} noticias correctamente</p>
            </div>
          </div>

          <div v-if="importErrors.length > 0" class="result-errors">
            <h5>Errores encontrados:</h5>
            <ul>
              <li v-for="(error, index) in importErrors" :key="index">
                {{ error }}
              </li>
            </ul>
          </div>
        </div>

        <div class="step-actions">
          <button @click="reset" class="btn-secondary">
            <i class="fas fa-redo"></i>
            Importar más
          </button>
          <button @click="$emit('close')" class="btn-primary">
            <i class="fas fa-check"></i>
            Finalizar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useNews } from '@/composables/useNews';

interface NewsImportItem {
  title: string;
  content: string;
  createdAt?: string;
  error?: string;
}

defineEmits<{
  close: []
}>();

const { createNews } = useNews();

const step = ref(1);
const isDragging = ref(false);
const selectedFile = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const parsing = ref(false);
const parsedNews = ref<NewsImportItem[]>([]);
const activeFormat = ref<'json' | 'csv'>('json');
const importing = ref(false);
const importProgress = ref(0);
const importedCount = ref(0);
const importErrors = ref<string[]>([]);

const validNews = computed(() => parsedNews.value.filter(item => !item.error));
const invalidNews = computed(() => parsedNews.value.filter(item => item.error));

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    selectedFile.value = target.files[0];
  }
};

const openFilePicker = () => {
  fileInput.value?.click();
};

const handleFileDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files[0]) {
    const file = files[0];
    if (file.name.endsWith('.json') || file.name.endsWith('.csv')) {
      selectedFile.value = file;
    } else {
      alert('Por favor selecciona un archivo JSON o CSV');
    }
  }
};

const removeFile = () => {
  selectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
};

const parseFile = async () => {
  if (!selectedFile.value) return;

  parsing.value = true;
  try {
    const text = await selectedFile.value.text();

    if (selectedFile.value.name.endsWith('.json')) {
      parseJSON(text);
    } else {
      parseCSV(text);
    }

    step.value = 2;
  } catch (error) {
    alert('Error al leer el archivo: ' + (error as Error).message);
  } finally {
    parsing.value = false;
  }
};

const parseJSON = (text: string) => {
  try {
    const data = JSON.parse(text);
    if (!Array.isArray(data)) {
      throw new Error('El archivo JSON debe contener un array de noticias');
    }

    parsedNews.value = data.map((item: any, index: number) => {
      const newsItem: NewsImportItem = {
        title: item.title || '',
        content: item.content || '',
        createdAt: item.createdAt
      };

      // Validation
      if (!newsItem.title.trim()) {
        newsItem.error = `Fila ${index + 1}: El título es obligatorio`;
      } else if (!newsItem.content.trim()) {
        newsItem.error = `Fila ${index + 1}: El contenido es obligatorio`;
      }

      return newsItem;
    });
  } catch (error) {
    alert('Error al parsear JSON: ' + (error as Error).message);
    parsedNews.value = [];
  }
};

const parseCSV = (text: string) => {
  const lines = text.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    alert('El archivo CSV está vacío o no tiene el formato correcto');
    return;
  }

  // Skip header row
  const dataLines = lines.slice(1);

  parsedNews.value = dataLines.map((line, index) => {
    const regex = /(?:\"([^\"]*(?:\"\"[^\"]*)*)\"|([^\",]+)|(?=,))/g;
    const fields: string[] = [];
    let match;

    while ((match = regex.exec(line)) !== null) {
      fields.push(match[1] ? match[1].replace(/""/g, '"') : (match[2] || ''));
    }

    const newsItem: NewsImportItem = {
      title: (fields[0] || '').trim(),
      content: (fields[1] || '').trim(),
      createdAt: fields[2] ? fields[2].trim() : undefined
    };

    // Validation
    if (!newsItem.title) {
      newsItem.error = `Fila ${index + 2}: El título es obligatorio`;
    } else if (!newsItem.content) {
      newsItem.error = `Fila ${index + 2}: El contenido es obligatorio`;
    }

    return newsItem;
  });
};

const importNews = async () => {
  importing.value = true;
  importProgress.value = 0;
  importedCount.value = 0;
  importErrors.value = [];

  for (const item of validNews.value) {
    try {
      const createdAt = item.createdAt ? new Date(item.createdAt) : undefined;
      const success = await createNews(item.title, item.content, createdAt);

      if (success) {
        importedCount.value++;
      } else {
        importErrors.value.push(`Error al importar: ${item.title}`);
      }
    } catch (error) {
      importErrors.value.push(`Error en "${item.title}": ${(error as Error).message}`);
    }

    importProgress.value++;
  }

  importing.value = false;
  step.value = 3;
};

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const reset = () => {
  step.value = 1;
  selectedFile.value = null;
  parsedNews.value = [];
  importProgress.value = 0;
  importedCount.value = 0;
  importErrors.value = [];
  if (fileInput.value) {
    fileInput.value.value = '';
  }
};
</script>

<style scoped>
.news-import {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 80vh;
}

.import-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-6);
  border-bottom: 1px solid var(--color-neutral-200);
  flex-shrink: 0;
}

.import-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.import-title i {
  color: var(--color-primary-600);
}

.close-button {
  background: none;
  border: none;
  color: var(--color-neutral-500);
  font-size: var(--font-size-xl);
  cursor: pointer;
  transition: var(--transition-all);
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
}

.close-button:hover {
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
}

.import-body {
  flex: 1;
  overflow-y: auto;
  padding: var(--spacing-6);
}

.import-step {
  max-width: 800px;
  margin: 0 auto;
}

.step-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
}

.step-number {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--color-primary-500), var(--color-primary-600));
  color: white;
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  flex-shrink: 0;
}

.step-info h4 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-1) 0;
}

.step-info p {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  margin: 0;
}

.file-upload-zone {
  margin-bottom: var(--spacing-6);
}

.file-input {
  display: none;
}

.upload-area {
  border: 2px dashed var(--color-neutral-300);
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-12) var(--spacing-6);
  text-align: center;
  cursor: pointer;
  transition: var(--transition-all);
  background: var(--color-neutral-50);
}

.upload-area:hover,
.drag-over .upload-area {
  border-color: var(--color-primary-400);
  background: var(--color-primary-50);
}

.upload-icon {
  font-size: 3rem;
  color: var(--color-primary-600);
  margin-bottom: var(--spacing-4);
}

.upload-text {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-700);
  margin: 0 0 var(--spacing-2) 0;
}

.upload-hint {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
  margin: 0;
}

.selected-file {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-4);
  background: var(--color-primary-50);
  border: 1px solid var(--color-primary-200);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-6);
}

.file-icon {
  width: 48px;
  height: 48px;
  background: var(--color-primary-100);
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-xl);
  color: var(--color-primary-700);
  flex-shrink: 0;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-900);
  margin-bottom: var(--spacing-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.remove-file {
  background: none;
  border: none;
  color: var(--color-error-600);
  cursor: pointer;
  padding: var(--spacing-2);
  border-radius: var(--border-radius-md);
  transition: var(--transition-all);
}

.remove-file:hover {
  background: var(--color-error-50);
}

.format-guide {
  background: var(--color-neutral-50);
  border: 1px solid var(--color-neutral-200);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-4);
  margin-bottom: var(--spacing-6);
}

.format-guide h5 {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-neutral-700);
  margin: 0 0 var(--spacing-3) 0;
}

.format-tabs {
  display: flex;
  gap: var(--spacing-2);
  margin-bottom: var(--spacing-3);
}

.format-tab {
  padding: var(--spacing-2) var(--spacing-4);
  background: white;
  border: 1px solid var(--color-neutral-300);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  color: var(--color-neutral-700);
}

.format-tab:hover {
  border-color: var(--color-primary-300);
  background: var(--color-primary-50);
}

.format-tab.active {
  background: var(--color-primary-600);
  border-color: var(--color-primary-600);
  color: white;
}

.format-example {
  background: var(--color-neutral-900);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-4);
  overflow-x: auto;
}

.format-example pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
}

.format-example code {
  color: #a5f3fc;
}

.validation-summary {
  display: flex;
  gap: var(--spacing-4);
  margin-bottom: var(--spacing-6);
}

.summary-item {
  flex: 1;
  padding: var(--spacing-4);
  border-radius: var(--border-radius-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  font-weight: var(--font-weight-medium);
}

.summary-item.valid {
  background: var(--color-success-50);
  color: var(--color-success-700);
  border: 1px solid var(--color-success-200);
}

.summary-item.invalid {
  background: var(--color-error-50);
  color: var(--color-error-700);
  border: 1px solid var(--color-error-200);
}

.summary-item i {
  font-size: var(--font-size-xl);
}

.news-preview-list {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--color-neutral-200);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-6);
}

.news-preview-item {
  padding: var(--spacing-4);
  border-bottom: 1px solid var(--color-neutral-200);
}

.news-preview-item:last-child {
  border-bottom: none;
}

.news-preview-item.invalid {
  background: var(--color-error-50);
}

.preview-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  margin-bottom: var(--spacing-2);
}

.preview-status {
  flex-shrink: 0;
}

.valid-icon {
  color: var(--color-success-600);
  font-size: var(--font-size-lg);
}

.invalid-icon {
  color: var(--color-error-600);
  font-size: var(--font-size-lg);
}

.preview-title {
  flex: 1;
  font-weight: var(--font-weight-medium);
  color: var(--color-neutral-900);
}

.preview-error {
  color: var(--color-error-700);
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.preview-meta {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  margin-left: var(--spacing-8);
}

.import-results {
  margin-bottom: var(--spacing-6);
}

.result-summary {
  display: flex;
  align-items: center;
  gap: var(--spacing-4);
  padding: var(--spacing-6);
  border-radius: var(--border-radius-lg);
  margin-bottom: var(--spacing-4);
}

.result-summary.success {
  background: var(--color-success-50);
  border: 1px solid var(--color-success-200);
  color: var(--color-success-700);
}

.result-summary i {
  font-size: 3rem;
}

.result-text h5 {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  margin: 0 0 var(--spacing-1) 0;
}

.result-text p {
  font-size: var(--font-size-base);
  margin: 0;
}

.result-errors {
  background: var(--color-error-50);
  border: 1px solid var(--color-error-200);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-4);
}

.result-errors h5 {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-error-700);
  margin: 0 0 var(--spacing-2) 0;
}

.result-errors ul {
  margin: 0;
  padding-left: var(--spacing-5);
  color: var(--color-error-600);
  font-size: var(--font-size-sm);
}

.step-actions {
  display: flex;
  gap: var(--spacing-3);
  justify-content: flex-end;
  padding-top: var(--spacing-6);
  border-top: 1px solid var(--color-neutral-200);
}

.btn-primary,
.btn-secondary {
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  border: none;
}

.btn-primary {
  background: var(--color-primary-600);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-primary-700);
  transform: translateY(-1px);
}

.btn-primary:disabled {
  background: var(--color-neutral-400);
  cursor: not-allowed;
  transform: none;
}

.btn-secondary {
  background: var(--color-neutral-100);
  color: var(--color-neutral-700);
  border: 1px solid var(--color-neutral-300);
}

.btn-secondary:hover {
  background: var(--color-neutral-200);
  border-color: var(--color-neutral-400);
}

@media (max-width: 640px) {
  .import-header,
  .import-body {
    padding: var(--spacing-4);
  }

  .step-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .validation-summary {
    flex-direction: column;
  }

  .step-actions {
    flex-direction: column-reverse;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    justify-content: center;
  }
}
</style>
